import { useState, useEffect } from 'react';

import { getPhaseLabel } from '@/components/GenerationProgress';
import type { GenerationProgress as ProgressType } from '@/types/presentation';

// ── Types ─────────────────────────────────────────────────

export interface ElapsedTimerProps {
  since: string;
  className?: string;
}

export interface StatusBadgeProps {
  status: string;
  since?: string;
  generationProgress?: ProgressType | null;
  styles: Record<string, string>;
}

// ── ElapsedTimer ──────────────────────────────────────────

export function ElapsedTimer({ since, className }: ElapsedTimerProps) {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    const start = new Date(since.replace(' ', 'T') + 'Z').getTime();
    if (isNaN(start)) return;

    const tick = () => {
      const diff = Math.max(0, Math.floor((Date.now() - start) / 1000));
      const m = Math.floor(diff / 60);
      const s = diff % 60;
      setElapsed(m > 0 ? `${m}m ${s}s` : `${s}s`);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [since]);

  return <span className={className}>{elapsed}</span>;
}

// ── StatusBadge ───────────────────────────────────────────

const WIZARD_STEP_LABELS = ['Planning', 'Slides', 'Images', 'Costs', 'Generate'] as const;

export function StatusBadge({ status, since, generationProgress, styles }: StatusBadgeProps) {
  const cls = (styles[`badge_${status}`] as string | undefined) ?? styles.badge_draft;

  const isWizardDraft = status === 'wizard_draft' && generationProgress?.wizard_step;
  const phaseText = status === 'generating' && generationProgress
    ? getPhaseLabel(generationProgress.phase, generationProgress.wizard_step)
    : isWizardDraft
      ? WIZARD_STEP_LABELS[(generationProgress!.wizard_step! - 1)] ?? 'En progreso'
      : null;

  const displayStatus = status === 'wizard_draft' ? 'En progreso' : status;
  const timerSince = status === 'generating' && generationProgress?.started_at
    ? generationProgress.started_at
    : since;

  return (
    <span className={`${styles.statusBadge} ${cls ?? ''}`}>
      {(status === 'generating' || status === 'wizard_draft') && <span className={styles.pulseDot} />}
      {phaseText ?? (displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1))}
      {status === 'generating' && timerSince && (
        <>
          {' '}
          &middot; <ElapsedTimer since={timerSince} className={styles.elapsedTimer} />
        </>
      )}
    </span>
  );
}
